import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Leaf, Sun, Moon, Droplets } from 'lucide-react';
import { Section } from '../../layout';
import { Button } from '../../ui/Button';
import { ServiceCard, ServiceCardProps } from './ServiceCard';
import { cn } from '../../../utils/cn';

// Mock service data
const mockServices: Omit<ServiceCardProps, 'className'>[] = [
  {
    id: 'swedish-massage',
    name: 'Swedish Massage',
    description:
      'A gentle, relaxing massage using long strokes and kneading techniques to ease muscle tension and promote overall wellness.',
    duration: '60-90 min',
    priceRange: '$80-$120',
    icon: Sparkles,
  },
  {
    id: 'deep-tissue',
    name: 'Deep Tissue Massage',
    description:
      'Targets deeper layers of muscle and connective tissue to release chronic tension and alleviate persistent pain.',
    duration: '60-90 min',
    priceRange: '$90-$140',
    icon: Heart,
  },
  {
    id: 'hot-stone',
    name: 'Hot Stone Therapy',
    description:
      'Heated basalt stones are placed on key points to melt away tension while promoting deep muscle relaxation.',
    duration: '75-90 min',
    priceRange: '$100-$150',
    icon: Sun,
  },
  {
    id: 'aromatherapy',
    name: 'Aromatherapy Massage',
    description:
      'Essential oils are combined with massage techniques to enhance relaxation, reduce stress, and uplift your mood.',
    duration: '60-90 min',
    priceRange: '$85-$130',
    icon: Leaf,
  },
  {
    id: 'sports-massage',
    name: 'Sports Massage',
    description:
      'Designed for athletes to prevent injuries, improve flexibility, and speed up recovery after intense physical activity.',
    duration: '60-90 min',
    priceRange: '$95-$145',
    icon: Moon,
  },
  {
    id: 'prenatal-massage',
    name: 'Prenatal Massage',
    description:
      'Specially tailored for expectant mothers to reduce pregnancy discomforts and promote relaxation safely.',
    duration: '60 min',
    priceRange: '$90-$120',
    icon: Droplets,
  },
];

export interface ServicesPreviewProps {
  /**
   * Additional CSS classes
   */
  className?: string;
}

export const ServicesPreview: FC<ServicesPreviewProps> = ({ className }) => {
  return (
    <Section
      spacing="lg"
      className={cn('bg-gray-50 dark:bg-slate-900', className)}
      aria-labelledby="services-heading"
    >
      {/* Section Header */}
      <div className="mb-12 text-center">
        <h2
          id="services-heading"
          className="mb-4 font-serif text-3xl font-bold text-gray-900 dark:text-slate-100 sm:text-4xl"
        >
          Our Services
        </h2>
        <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-slate-400">
          Discover our range of therapeutic massage treatments designed to restore balance, relieve
          tension, and promote your overall well-being.
        </p>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {mockServices.map((service) => (
          <ServiceCard key={service.id} {...service} />
        ))}
      </div>

      {/* View All Button */}
      <div className="mt-12 text-center">
        <Link to="/services">
          <Button variant="primary" size="lg">
            View All Services
          </Button>
        </Link>
      </div>
    </Section>
  );
};

ServicesPreview.displayName = 'ServicesPreview';
